import React, { useState, useRef, useEffect } from 'react';

/**
 * Chat público de VENDAS da Liz (landing page).
 * Conversa com a Liz consultora comercial via POST /api/ia/publico/vendas
 * (endpoint @Public, sem tenant) — humanizada, com memória curta da conversa.
 */
interface Mensagem {
  autor: 'liz' | 'voce';
  texto: string;
}

const SUGESTOES = [
  'Quais módulos o sistema oferece?',
  'Quais são os planos e preços?',
  'Quais as vantagens do Gestão Creches?',
  'Como faço para assinar?',
];

export default function LizSalesChat({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [mensagens, setMensagens] = useState<Mensagem[]>([
    {
      autor: 'liz',
      texto:
        'Oi! Eu sou a Liz 💜 Posso te explicar tudo sobre o Gestão Creches — módulos, planos, vantagens e como começar. O que você gostaria de saber?',
    },
  ]);
  const [input, setInput] = useState('');
  const [carregando, setCarregando] = useState(false);
  const fimRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens, carregando]);

  if (!open) return null;

  async function enviar(texto: string) {
    const pergunta = texto.trim();
    if (!pergunta || carregando) return;
    const historico = mensagens.slice(-8); // mensagens anteriores; o backend anexa a pergunta atual
    setMensagens((m) => [...m, { autor: 'voce', texto: pergunta }]);
    setInput('');
    setCarregando(true);
    try {
      const resp = await fetch('/api/ia/publico/vendas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pergunta, historico }),
      });
      const data = await resp.json().catch(() => ({} as any));
      const resposta =
        (data && data.resposta) ||
        'Desculpe, não consegui responder agora. Tente novamente em instantes ou fale com comercial@gestaocreche.com.br.';
      setMensagens((m) => [...m, { autor: 'liz', texto: resposta }]);
    } catch {
      setMensagens((m) => [
        ...m,
        { autor: 'liz', texto: 'Tive um problema de conexão. Pode tentar de novo? Ou fale com comercial@gestaocreche.com.br.' },
      ]);
    } finally {
      setCarregando(false);
    }
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Converse com a Liz">
        {/* Header */}
        <div style={S.header}>
          <img src="/liz_avatar.png" alt="Liz" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover' }} />
          <div style={{ flex: 1 }}>
            <strong style={{ display: 'block', fontSize: 15 }}>Liz</strong>
            <span style={{ fontSize: 12, opacity: 0.85 }}>Consultora do Gestão Creches · online</span>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={S.fechar}>×</button>
        </div>

        {/* Mensagens */}
        <div style={S.msgs}>
          {mensagens.map((m, i) => (
            <div key={i} style={{ ...S.msg, ...(m.autor === 'voce' ? S.msgVoce : S.msgLiz) }}>
              {m.texto.split('\n').map((linha, j) => (
                <React.Fragment key={j}>
                  {linha}
                  {j < m.texto.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
          ))}
          {carregando && <div style={{ ...S.msg, ...S.msgLiz, fontStyle: 'italic', opacity: 0.7 }}>Liz está digitando…</div>}

          {mensagens.length <= 1 && !carregando && (
            <div style={S.sugestoes}>
              {SUGESTOES.map((s) => (
                <button key={s} style={S.chip} onClick={() => enviar(s)}>{s}</button>
              ))}
            </div>
          )}
          <div ref={fimRef} />
        </div>

        {/* Input */}
        <form
          style={S.inputBar}
          onSubmit={(e) => { e.preventDefault(); enviar(input); }}
        >
          <input
            style={S.input}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Pergunte sobre módulos, planos, vantagens…"
            autoFocus
          />
          <button type="submit" style={S.enviar} disabled={carregando || !input.trim()} aria-label="Enviar">➤</button>
        </form>
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
    zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: 20,
  },
  panel: {
    width: 'min(420px, 100%)', height: 'min(620px, 90vh)', background: '#fff', borderRadius: 18,
    display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', color: '#fff',
    background: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)',
  },
  avatar: {
    width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18,
  },
  fechar: {
    background: 'transparent', border: 'none', color: '#fff', fontSize: 26, cursor: 'pointer', lineHeight: 1, padding: 0, width: 30,
  },
  msgs: { flex: 1, overflowY: 'auto', padding: 16, background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 10 },
  msg: { maxWidth: '85%', padding: '10px 14px', borderRadius: 14, fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap' },
  msgLiz: { alignSelf: 'flex-start', background: '#fff', color: '#1e293b', border: '1px solid #e5e7eb', borderTopLeftRadius: 4 },
  msgVoce: { alignSelf: 'flex-end', background: '#2563eb', color: '#fff', borderTopRightRadius: 4 },
  sugestoes: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  chip: {
    background: '#eef2ff', color: '#2563eb', border: '1px solid #c7d2fe', borderRadius: 999,
    padding: '7px 12px', fontSize: 13, cursor: 'pointer',
  },
  inputBar: { display: 'flex', gap: 8, padding: 12, borderTop: '1px solid #e5e7eb', background: '#fff' },
  input: {
    flex: 1, border: '1px solid #cbd5e1', borderRadius: 999, padding: '10px 16px', fontSize: 14, outline: 'none',
  },
  enviar: {
    width: 42, height: 42, borderRadius: '50%', border: 'none', background: '#2563eb', color: '#fff',
    fontSize: 16, cursor: 'pointer', flexShrink: 0,
  },
};
